import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Subscription, take } from 'rxjs';

import { User } from 'src/app/core/models/user.model';
import { TTSApiService } from 'src/app/core/services/tts-api.service';
import { UserService } from 'src/app/core/services/user.service';
import {
    ZtButtonConfig,
    ZtButtonType,
} from 'src/app/shared/components/atoms/zt-button/zt-button.model';
import { ZtIcon } from 'src/app/shared/components/atoms/zt-icon/zt-icon.model';
import { ZtTextInputConfig } from 'src/app/shared/components/atoms/zt-textinput/zt-textinput.model';
import { ZtLoaderService } from 'src/app/shared/components/molecules/zt-loader/zt-loader.service';

import { APILicense, APILicenseCheckerResponse } from './user-profile.model';

@UntilDestroy()
@Component({
    selector: 'zt-user-profile',
    templateUrl: 'user-profile.component.html',
    styleUrls: ['user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit, AfterViewInit, OnDestroy {
    private _ttsSubscription = new Subscription();
    private _licenseRequestSent = false;

    license: APILicense | null = null;
    currentUser: User | null = null;

    // general information fields config
    firstNameConfig: ZtTextInputConfig = {
        label: 'First Name',
        name: 'first-name',
        id: 'first-name',
    };
    lastNameConfig: ZtTextInputConfig = {
        label: 'Last Name',
        name: 'last-name',
        id: 'last-name',
    };
    emailConfig: ZtTextInputConfig = {
        label: 'Email',
        name: 'email',
        id: 'email',
    };

    // password information fields config
    newPassConfig: ZtTextInputConfig = {
        label: 'New password',
        name: 'new-password',
        id: 'new-password',
        type: 'text',
    };
    confirmNewPassConfig: ZtTextInputConfig = {
        label: 'Confirm password',
        name: 'confirm-password',
        id: 'confirm-password',
        type: 'text',
    };
    saveBtnConfig: ZtButtonConfig = {
        label: 'Save',
        name: 'save',
        id: 'save',
        icon: ZtIcon.SAVE,
    };
    changePasswordForm = new FormGroup({
        newPass: new FormControl<string>(''),
        confirmPass: new FormControl<string>(''),
    });

    // license information fields config
    apiKeyConfig: ZtTextInputConfig = {
        label: 'API Key',
        name: 'api-key',
        id: 'api-key',
    };
    apiKeyRemCharsConfig: ZtTextInputConfig = {
        label: 'Remaining characters',
        name: 'api-key-rem-chars',
        id: 'api-key-rem-chars',
    };
    apiKeyExpiringDateConfig: ZtTextInputConfig = {
        label: 'Expiring date',
        name: 'api-key-expiring-date',
        id: 'api-key-expiring-date',
    };

    constructor(
        private userService: UserService,
        private ttsService: TTSApiService,
        private loadingService: ZtLoaderService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.currentUser = this.userService.getCurrentUser();
        this.loadingService.showLoader();
        this.ttsService
            .getConnection()
            .pipe(untilDestroyed(this))
            .subscribe((connection: any) => {
                if (connection) {
                    this._ttsSubscription.unsubscribe();
                    this._ttsSubscription = connection.subscribe(
                        (response: any) => {
                            console.log('Received API License response:', response);
                            
                            // Response might be a JSON string, parse if necessary
                            let parsedResponse: APILicenseCheckerResponse = response;
                            if (typeof response === 'string') {
                                try {
                                    parsedResponse = JSON.parse(response);
                                    console.log('Parsed response from string:', parsedResponse);
                                } catch (e) {
                                    console.error('Failed to parse response as JSON:', response);
                                    this.loadingService.hideLoader();
                                    this.messageService.add({
                                        severity: 'error',
                                        summary: 'Invalid response format',
                                        detail: 'License information could not be processed',
                                    });
                                    return;
                                }
                            }

                            // Validate response has required fields
                            if (parsedResponse && parsedResponse.license) {
                                this.license = {
                                    key: parsedResponse.license,
                                    expiringDate: parsedResponse['expiring-date'] || 'N/A',
                                    remainingChar: parsedResponse['remaining-characters'] || 0,
                                } as APILicense;
                                console.log('Mapped license:', this.license);
                            } else {
                                console.error('Invalid response format - missing license field:', parsedResponse);
                                this.messageService.add({
                                    severity: 'error',
                                    summary: 'Invalid response',
                                    detail: 'License information could not be processed',
                                });
                            }
                            this.loadingService.hideLoader();
                        },
                        (error: any) => {
                            console.error('Error in license subscription:', error);
                            this.loadingService.hideLoader();
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Connection error',
                                detail: 'Failed to retrieve license information',
                            });
                        }
                    );
                }
            });

        this.ttsService.connect();
    }

    ngAfterViewInit(): void {
        // Try to send the license request, retry if currentUser is not yet loaded
        this.sendLicenseRequest(0);
    }

    private sendLicenseRequest(retryCount: number): void {
        if (this._licenseRequestSent) {
            return;
        }

        // Refresh currentUser from service in case it wasn't loaded initially
        this.currentUser = this.userService.getCurrentUser();

        if (!this.currentUser?.apiKey) {
            if (retryCount < 3) {
                console.warn(
                    `API Key not available (attempt ${retryCount + 1}/3). Retrying...`
                );
                // Retry after a short delay
                setTimeout(() => this.sendLicenseRequest(retryCount + 1), 500);
                return;
            } else {
                console.error('API Key is not available after retries');
                this.loadingService.hideLoader();
                this.messageService.add({
                    severity: 'error',
                    summary: 'Unable to load license information',
                    detail: 'API Key is missing from user profile',
                });
                return;
            }
        }

        this._licenseRequestSent = true;
        const message = {
            config: {
                get_key_info: this.currentUser.apiKey,
            },
        };

        console.log('Sending license check message with apiKey:', this.currentUser.apiKey);
        this.ttsService.sendMessage(message);
    }

    ngOnDestroy(): void {
        this._ttsSubscription.unsubscribe();
        this.ttsService.close();
    }

    onSubmitListener(event: Event): void {
        event.preventDefault();
    }

    changePassword(): void {
        this.loadingService.showLoader();
        this.userService
            .changePass(
                this.changePasswordForm.value.newPass!,
                this.changePasswordForm.value.confirmPass!
            )
            .pipe(take(1))
            .subscribe({
                next: (response: any) => {
                    this.loadingService.hideLoader();
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Changed the password successfully.',
                    });
                },
                error: (error: any) => {
                    let errorMessage = '';
                    if (
                        error.error?.password1 &&
                        Array.isArray(error.error?.password1)
                    ) {
                        errorMessage = (error.error.password1 as string[]).join(
                            ' '
                        );
                    } else if (
                        error.error?.password2 &&
                        Array.isArray(error.error?.password2)
                    ) {
                        errorMessage = (error.error.password2 as string[]).join(
                            ' '
                        );
                    } else if (
                        error.error?.password &&
                        Array.isArray(error.error?.password)
                    ) {
                        errorMessage = (error.error.password as string[]).join(
                            ' '
                        );
                    } else {
                        errorMessage =
                            'Something went wrong and the password could not be changed.';
                    }

                    this.messageService.add({
                        severity: 'error',
                        summary: errorMessage,
                    });
                    this.loadingService.hideLoader();
                },
            });
    }

    get ZtButtonType(): typeof ZtButtonType {
        return ZtButtonType;
    }

    get ZtIcon(): typeof ZtIcon {
        return ZtIcon;
    }

    get newPassControl(): AbstractControl<string | null, string | null> | null {
        return this.changePasswordForm.get('newPass');
    }

    get confirmPassControl(): AbstractControl<
        string | null,
        string | null
    > | null {
        return this.changePasswordForm.get('confirmPass');
    }
}
